import WorkshopDocumentsPanel from '../components/accounting/WorkshopDocumentsPanel';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileText, MessageCircle, RefreshCw, Send, WalletCards } from 'lucide-react';
import { financeAPI, vehicleFinanceAPI } from '../services/api.js';
import { useToast } from '../hooks/use-toast.js';
import DebtWhatsAppComposerDialog from '../components/DebtWhatsAppComposerDialog.js';
import ConfirmPaymentDialog from '../components/ConfirmPaymentDialog.js';
import { buildDebtWhatsAppDraft } from '../utils/debtWhatsapp.js';
import { getWhatsAppLink } from '../utils/constants.js';
import { useLocation, useNavigate } from 'react-router-dom';
import { FINANCE_TERMS } from '../utils/operationalFinanceTerms.js';
import { hasPermission } from '../utils/permissions.js';

const money = (value) => Number(value || 0).toLocaleString('ar-SA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const displayDate = (value) => {
    if (!value)
        return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('ar-SA');
};

const newPaymentKey = (visitId) => {
    const suffix = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `debt-followup:${visitId}:${suffix}`;
};

const readFailureMessage = (error) => {
    const detail = error?.response?.data?.detail;
    const code = (typeof detail === 'string' ? detail : detail?.error || detail?.code) || error?.message;
    if (code === 'CANONICAL_AR_SINGLE_PROJECT_SCOPE_NOT_CONFIGURED'
        || code === 'GLOBAL_FINANCIAL_SYSTEM_NOT_CLOSED') {
        return 'متابعة الذمم غير مفعّلة لهذه الورشة؛ إعدادها والتحقق المالي لم يكتملَا بعد. تعذّر تحديد الرصيد، وهذا لا يعني أن الذمم صفر. الدفعات المسجلة متاحة داخل ملفات المركبات والزيارات.';
    }
    if (code === 'CANONICAL_AR_WORKSHOP_SCOPE_MISMATCH') {
        return 'إعداد الورشة لا يطابق مصدر الذمم. تعذّر تحديد الرصيد؛ يلزم تصحيح الإعداد قبل عرض الذمم أو تحصيلها من هذه الصفحة.';
    }
    if (code === 'CANONICAL_AR_OPEN_ITEMS_REQUIRED' || code === 'CANONICAL_AR_CONTRACT_INVALID'
        || code === 'CANONICAL_AR_INTEGRITY_ERROR') {
        return 'بيانات الذمم غير مكتملة أو غير متسقة. تعذّر تحديد الرصيد؛ لن تتاح المطالبة أو التحصيل من هذه الصفحة حتى تُراجع البيانات.';
    }
    return 'تعذّر الاتصال بمصدر ذمم الزيارات. الرصيد غير متاح حاليًا؛ أعد المحاولة. لا يعني ذلك عدم وجود ذمم.';
};

const isAmount = (value) => (typeof value === 'number'
    || (typeof value === 'string' && value.trim() !== '')) && Number.isFinite(Number(value));

const sameMoney = (left, right) => Math.abs(Number(left) - Number(right)) <= 0.005;

export default function DebtFollowUp() {
    const navigate = useNavigate();
    const location = useLocation();
    const { toast } = useToast();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const loadRequestRef = useRef(0);
    const readReadyRef = useRef(false);
    const [manualAmounts, setManualAmounts] = useState({});
    const [savingRowId, setSavingRowId] = useState('');
    const [confirmPayRow, setConfirmPayRow] = useState(null);
    const [confirmPayLoading, setConfirmPayLoading] = useState(false);
    // Refs make the idempotency key and the in-flight lock synchronous.  State
    // alone leaves a short window where two clicks can issue two different
    // payment keys before React has rendered the first state change.
    const paymentKeysRef = useRef(new Map());
    const settlementLocksRef = useRef(new Set());
    const [selectedIds, setSelectedIds] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [drafts, setDrafts] = useState([]);
    const session = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('session') || '{}');
        }
        catch {
            return {};
        }
    }, []);
    const canSettle = hasPermission(session, 'operations', 'settle');

    const fetchData = useCallback(async () => {
        const requestId = ++loadRequestRef.current;
        readReadyRef.current = false;
        setLoading(true);
        setLoadError('');
        setSelectedIds([]);
        setDialogOpen(false);
        setDrafts([]);
        setConfirmPayRow(null);
        try {
            const asOf = new Date().toISOString().slice(0, 10);
            const response = await financeAPI.getCurrentAR({ as_of: asOf, include_today: true });
            if (requestId !== loadRequestRef.current) return;
            const payload = response?.data?.data;
            const customers = payload?.customers;
            if (payload?.engine_version !== 'canonical-visit-ar-v2'
                || !Array.isArray(customers)
                || !isAmount(payload?.total_ar)) {
                throw new Error('CANONICAL_AR_CONTRACT_INVALID');
            }

            const customersMissingOpenItems = customers.some((customer) =>
                !isAmount(customer?.balance)
                || (Number(customer?.balance) > 0 && !Array.isArray(customer?.open_items)));
            if (customersMissingOpenItems) {
                throw new Error('CANONICAL_AR_OPEN_ITEMS_REQUIRED');
            }

            const customersOutOfBalance = customers.some((customer) => {
                const itemTotal = (customer.open_items || []).reduce((sum, item) => {
                    const remaining = item?.remaining_amount ?? item?.display_remaining;
                    return sum + (isAmount(remaining) ? Number(remaining) : Number.NaN);
                }, 0);
                return !Number.isFinite(itemTotal) || !sameMoney(customer.balance, itemTotal);
            });
            if (customersOutOfBalance) {
                throw new Error('CANONICAL_AR_INTEGRITY_ERROR');
            }

            const canonicalEntries = customers.flatMap((customer, customerIndex) =>
                (customer.open_items || []).map((item, itemIndex) => {
                    const visitId = String(item?.visit_id || item?.allocation_id || '').trim();
                    const rawRemaining = item?.remaining_amount ?? item?.display_remaining;
                    const rawDue = item?.due_amount ?? item?.customer_total;
                    const rawPaid = item?.paid_amount ?? item?.ar_applied;
                    if (!visitId || !isAmount(rawRemaining) || !isAmount(rawDue) || !isAmount(rawPaid)) {
                        throw new Error('CANONICAL_AR_CONTRACT_INVALID');
                    }
                    const remaining = Number(rawRemaining);
                    if (remaining <= 0) {
                        return null;
                    }
                    return {
                        id: visitId,
                        entityType: 'customer',
                        source: 'canonical_visit_ar',
                        customerId: customer?.customer_id || customer?.id || `customer-${customerIndex}`,
                        name: customer?.customer || customer?.name || 'عميل',
                        phone: customer?.phone || '',
                        visitId,
                        vehicleId: item?.vehicle_id || '',
                        plate: item?.plate || '',
                        date: item?.date || item?.finalized_at || '',
                        dueAmount: Number(rawDue),
                        paidAmount: Number(rawPaid),
                        ajelBalance: remaining,
                        debitBalance: remaining,
                        creditBalance: 0,
                        currency: item?.currency || 'SAR',
                        itemIndex,
                    };
                }).filter(Boolean),
            );
            const openItemsTotal = canonicalEntries.reduce((sum, entry) => sum + entry.ajelBalance, 0);
            if (!sameMoney(payload.total_ar, openItemsTotal)) {
                throw new Error('CANONICAL_AR_INTEGRITY_ERROR');
            }
            readReadyRef.current = true;
            setEntries(canonicalEntries);
            setSelectedIds((current) => current.filter((id) => canonicalEntries.some((entry) => `${entry.entityType}-${entry.id}` === id)));
        }
        catch (error) {
            if (requestId !== loadRequestRef.current) return;
            setEntries([]);
            setLoadError(readFailureMessage(error));
        }
        finally {
            if (requestId === loadRequestRef.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        const onFinanceUpdated = () => fetchData();
        window.addEventListener('finance:updated', onFinanceUpdated);
        return () => {
            window.removeEventListener('finance:updated', onFinanceUpdated);
            ++loadRequestRef.current;
            readReadyRef.current = false;
        };
    }, [fetchData]);

    const requestedCustomerId = useMemo(() => String(new URLSearchParams(location.search || '').get('customerId') || '').trim(), [location.search]);
    const visibleEntries = useMemo(() => requestedCustomerId
        ? entries.filter((entry) => String(entry.customerId || '') === requestedCustomerId)
        : entries, [entries, requestedCustomerId]);

    const totals = useMemo(() => ({
        receivable: visibleEntries.reduce((sum, entry) => sum + Number(entry.ajelBalance || 0), 0),
        customers: new Set(visibleEntries.map((entry) => String(entry.customerId))).size,
        visits: visibleEntries.length,
    }), [visibleEntries]);

    const selectedEntries = useMemo(() => visibleEntries.filter((entry) =>
        selectedIds.includes(`${entry.entityType}-${entry.id}`)), [visibleEntries, selectedIds]);

    const openPreviewForRows = (rows) => {
        if (!readReadyRef.current) return;
        const nextDrafts = rows.map((row) => buildDebtWhatsAppDraft(row, 'customer'));
        if (!nextDrafts.length) {
            toast({ title: 'تنبيه', description: 'لا توجد ذمم محددة للمعاينة.' });
            return;
        }
        setDrafts(nextDrafts);
        setDialogOpen(true);
    };

    const updateDraftMessage = (draftId, message) => {
        setDrafts((current) => current.map((draft) => draft.id === draftId
            ? { ...draft, message, url: draft.phone ? getWhatsAppLink(draft.phone, message) : '' }
            : draft));
    };

    const sendCurrent = (draft) => {
        if (!readReadyRef.current) return;
        if (!draft?.phone || !draft?.url) {
            toast({ title: 'تنبيه', description: 'لا يوجد رقم واتساب صالح لهذه الذمة.', variant: 'destructive' });
            return;
        }
        window.open(draft.url, '_blank', 'noopener,noreferrer');
    };

    const sendAll = (allDrafts) => {
        if (!readReadyRef.current) return;
        const validDrafts = (allDrafts || []).filter((draft) => draft.phone && draft.url);
        if (!validDrafts.length) {
            toast({ title: 'تنبيه', description: 'لا توجد رسائل صالحة للإرسال.', variant: 'destructive' });
            return;
        }
        validDrafts.forEach((draft, index) => {
            window.setTimeout(() => window.open(draft.url, '_blank', 'noopener,noreferrer'), index * 280);
        });
        toast({ title: 'تم التنفيذ', description: `تم فتح ${validDrafts.length} رسالة بعد المعاينة.` });
    };

    const createSettlementOrder = async (row, paymentLines, date) => {
        if (!canSettle) {
            toast({ title: 'صلاحية غير كافية', description: 'لا تملك صلاحية تسوية دفعات الزيارات.', variant: 'destructive' });
            return;
        }
        if (!readReadyRef.current) return;
        const rowId = `${row.entityType}-${row.id}`;
        const remaining = Number(row.ajelBalance || 0);
        const manualValue = manualAmounts[rowId];
        const requestedAmount = manualValue === undefined || manualValue === '' ? remaining : Number(manualValue);
        if (!row.visitId) {
            toast({ title: 'خطأ', description: 'هذه الذمة لا تحمل معرف زيارة قانوني.', variant: 'destructive' });
            return;
        }
        if (!Number.isFinite(requestedAmount) || requestedAmount <= 0 || requestedAmount > remaining + 0.005) {
            toast({ title: 'تنبيه', description: 'أدخل مبلغًا موجبًا لا يتجاوز المتبقي لهذه الزيارة.', variant: 'destructive' });
            return;
        }
        if (!paymentLines) {
            setConfirmPayRow({ ...row, defaultAmount: requestedAmount });
            return;
        }

        const enteredLines = paymentLines
            .map((line) => ({ ...line, amount: Number(line?.amount || 0) }))
            .filter((line) => Number.isFinite(line.amount) && line.amount > 0);
        const lines = enteredLines.length
            ? enteredLines
            : [{ method: paymentLines[0]?.method || 'bank_transfer', amount: requestedAmount }];
        const total = lines.reduce((sum, line) => sum + line.amount, 0);
        if (total <= 0 || total > requestedAmount + 0.005 || total > remaining + 0.005) {
            toast({ title: 'تنبيه', description: 'إجمالي وسائل الدفع يجب ألا يتجاوز رصيد الزيارة.', variant: 'destructive' });
            return;
        }

        const signature = JSON.stringify({
            visitId: row.visitId,
            date,
            lines: lines.map((line) => ({ method: line.method, amount: line.amount })),
        });
        const lockKey = `${row.visitId}:${signature}`;
        if (settlementLocksRef.current.has(lockKey)) {
            return;
        }
        settlementLocksRef.current.add(lockKey);
        const baseKey = paymentKeysRef.current.get(signature) || newPaymentKey(row.visitId);
        paymentKeysRef.current.set(signature, baseKey);

        setSavingRowId(rowId);
        try {
            for (const [index, line] of lines.entries()) {
                await vehicleFinanceAPI.confirmVisitPayment(row.visitId, {
                    amount: line.amount,
                    method: line.method,
                    date,
                    reference: `Debt follow-up visit ${row.visitId}`,
                    idempotencyKey: `${baseKey}:${index}`,
                });
            }
            paymentKeysRef.current.delete(signature);
            setManualAmounts((current) => ({ ...current, [rowId]: '' }));
            setConfirmPayRow(null);
            toast({ title: 'تم تسجيل السداد', description: `تم تطبيق ${money(total)} ر.س على الزيارة المحددة.` });
            window.dispatchEvent(new Event('finance:updated'));
            await fetchData();
        }
        catch (error) {
            console.error('Canonical visit payment failed:', error);
            const message = error?.response?.data?.detail || error?.message || 'تعذر تسجيل السداد القانوني للزيارة.';
            toast({ title: 'خطأ في السداد', description: message, variant: 'destructive' });
        }
        finally {
            settlementLocksRef.current.delete(lockKey);
            setSavingRowId('');
        }
    };

    return (
        <div className="p-3 sm:p-5 space-y-4" dir="rtl" data-testid="debt-followup-page">
            <section className="rounded-2xl border border-cyan-400/20 bg-slate-950/50 p-4 sm:p-5 text-slate-100">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold">متابعة ذمم الزيارات</h1>
                        <p className="mt-1 text-sm text-slate-300">كل صف يمثل زيارة نهائية واحدة؛ السداد يمر عبر سجل دفعات تلك الزيارة فقط.</p>
                        {requestedCustomerId && <p className="mt-1 text-xs text-cyan-100" data-testid="debt-customer-filter-active">عرض ذمم العميل المحدد فقط.</p>}
                    </div>
                    <button type="button" onClick={fetchData} className="apple-button-secondary h-10 px-3 self-start inline-flex items-center gap-2" disabled={loading} data-testid="debt-refresh-button">
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> تحديث
                    </button>
                </div>
                {!loading && !loadError && <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="debt-totals">
                    <div className="rounded-xl bg-cyan-500/10 p-3"><p className="text-xs text-cyan-100/80">{FINANCE_TERMS.workshop} على العملاء</p><p className="mt-1 font-bold text-cyan-50">{money(totals.receivable)} ر.س</p></div>
                    <div className="rounded-xl bg-white/5 p-3"><p className="text-xs text-slate-300">العملاء</p><p className="mt-1 font-bold">{totals.customers}</p></div>
                    <div className="rounded-xl bg-white/5 p-3"><p className="text-xs text-slate-300">بنود الزيارات المفتوحة</p><p className="mt-1 font-bold">{totals.visits}</p></div>
                </div>}
            </section>

            <WorkshopDocumentsPanel title="ذمم الحركات المستقلة" customerId={requestedCustomerId || undefined} outstandingOnly canCreate={false}/>
            {loading && <div role="status" className="py-10 text-center text-slate-500" data-testid="debt-followup-loading">جارٍ تحميل ذمم الزيارات…</div>}

            {!loading && loadError && <section role="alert" className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-slate-900" data-testid="debt-load-unavailable">
                <h2 className="font-bold">رصيد الذمم غير متاح</h2>
                <p className="mt-2 text-sm leading-7" data-testid="debt-load-error">{loadError}</p>
                <p className="mt-2 text-sm leading-7">لمراجعة الدفعات، افتح ملف المركبة ثم الزيارة المطلوبة. المركبات المسلّمة موجودة في الأرشيف. تسليم المركبة أو أرشفتها لا يعتمد ذمتها ماليًا تلقائيًا.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" className="rounded-lg border border-slate-400 bg-white px-3 py-2 text-sm" onClick={() => navigate('/')} data-testid="debt-view-vehicles">المركبات الحالية</button>
                    <button type="button" className="rounded-lg border border-slate-400 bg-white px-3 py-2 text-sm" onClick={() => navigate('/archive')} data-testid="debt-view-archive">أرشيف المركبات</button>
                </div>
            </section>}

            {!loading && !loadError && <section className="rounded-2xl border border-white/10 bg-white/5 p-3 sm:p-4" data-testid="debt-followup-table-wrapper">
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-slate-300">عدد بنود الذمم: <b className="text-cyan-100" data-testid="debt-followup-entries-count">{visibleEntries.length}</b></p>
                    <div className="flex gap-2">
                        <button type="button" className="apple-button-secondary h-10 px-3" disabled={!visibleEntries.length} onClick={() => setSelectedIds(visibleEntries.map((row) => `${row.entityType}-${row.id}`))}>تحديد الكل</button>
                        <button type="button" className="apple-button h-10 px-3 inline-flex items-center gap-1" disabled={!selectedEntries.length} onClick={() => openPreviewForRows(selectedEntries)}><MessageCircle size={15} /> معاينة الرسائل</button>
                    </div>
                </div>

                {visibleEntries.length === 0 ? <div className="py-10 text-center text-slate-400" data-testid="debt-followup-empty">لا توجد ذمم مفتوحة لزيارات معتمدة ماليًا{requestedCustomerId ? ' لهذا العميل' : ''}. الزيارات غير المعتمدة ماليًا لا تدخل في هذا الرصيد، حتى لو تم تسليم المركبة.</div>
                        : <div className="space-y-3" data-testid="debt-followup-open-items">
                            {visibleEntries.map((row) => {
                                const rowId = `${row.entityType}-${row.id}`;
                                const selected = selectedIds.includes(rowId);
                                return <article key={rowId} className="overflow-hidden rounded-xl border border-white/10 bg-slate-950/35" data-testid={`debt-row-${rowId}`}>
                                    <div className="p-3 sm:p-4">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                        <div className="flex items-start gap-3 min-w-0">
                                            <input type="checkbox" checked={selected} onChange={(event) => setSelectedIds((current) => event.target.checked ? [...current, rowId] : current.filter((id) => id !== rowId))} className="mt-1 h-4 w-4 accent-cyan-400" aria-label={`تحديد ${row.name}`} />
                                            <div className="min-w-0">
                                                <p className="font-bold text-slate-50 break-words">{row.name}</p>
                                                <p className="mt-1 text-xs font-medium text-cyan-100">مصدر الرصيد: زيارة معتمدة</p>
                                            </div>
                                        </div>
                                        <div className="rounded-lg border border-rose-300/20 bg-rose-400/10 px-3 py-2 text-right sm:min-w-40"><p className="text-xs text-rose-100/80">المتبقي {FINANCE_TERMS.workshop}</p><p className="mt-1 text-lg font-extrabold tabular-nums text-rose-100">{money(row.ajelBalance)} ر.س</p></div>
                                    </div>
                                    <dl className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
                                        <div className="min-w-0 rounded-lg bg-white/5 px-3 py-2"><dt className="text-slate-400">لوحة المركبة</dt><dd className="mt-1 break-words font-semibold text-slate-100">{row.plate || 'غير مسجلة'}</dd></div>
                                        <div className="min-w-0 rounded-lg bg-white/5 px-3 py-2"><dt className="text-slate-400">تاريخ الزيارة</dt><dd className="mt-1 break-words font-semibold text-slate-100">{displayDate(row.date)}</dd></div>
                                    </dl>
                                    <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
                                        <div className="min-w-0 rounded-lg border border-white/10 bg-white/5 px-3 py-2"><dt className="text-slate-400">إجمالي الزيارة</dt><dd className="mt-1 truncate font-semibold tabular-nums text-slate-100">{money(row.dueAmount)} ر.س</dd></div>
                                        <div className="min-w-0 rounded-lg border border-white/10 bg-white/5 px-3 py-2"><dt className="text-slate-400">المدفوع المسجل</dt><dd className="mt-1 truncate font-semibold tabular-nums text-slate-100">{money(row.paidAmount)} ر.س</dd></div>
                                    </dl>
                                    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                                        {canSettle && <>
                                            <input type="number" min="0" max={row.ajelBalance} step="0.01" value={manualAmounts[rowId] ?? ''} onChange={(event) => setManualAmounts((current) => ({ ...current, [rowId]: event.target.value }))} placeholder={`حتى ${Number(row.ajelBalance).toFixed(2)}`} className="h-10 w-full sm:w-40 rounded-lg border border-white/20 bg-white/10 px-3 text-sm text-white placeholder:text-slate-400 focus:border-cyan-300 focus:outline-none" data-testid={`debt-row-manual-amount-${rowId}`} />
                                            <button type="button" className="h-10 rounded-lg border border-cyan-400/40 bg-cyan-500/15 px-3 text-sm font-semibold text-cyan-100 disabled:opacity-60" onClick={() => createSettlementOrder(row)} disabled={savingRowId === rowId} data-testid={`debt-row-settlement-order-${rowId}`}><span className="inline-flex items-center gap-1"><WalletCards size={15} /> {savingRowId === rowId ? 'جارٍ...' : 'سداد الزيارة'}</span></button>
                                        </>}
                                        {row.vehicleId && <button type="button" className="h-10 rounded-lg border border-white/20 px-3 text-sm text-slate-200" onClick={() => navigate(`/vehicle/${encodeURIComponent(row.vehicleId)}`)}><span className="inline-flex items-center gap-1"><FileText size={15} /> بطاقة الزيارة</span></button>}
                                        <button type="button" className="h-10 rounded-lg border border-emerald-400/40 bg-emerald-500/15 px-3 text-sm text-emerald-100" onClick={() => openPreviewForRows([row])}><span className="inline-flex items-center gap-1"><Send size={15} /> واتساب</span></button>
                                    </div>
                                    </div>
                                </article>;
                            })}
                        </div>}
            </section>}

            <DebtWhatsAppComposerDialog open={dialogOpen} onOpenChange={setDialogOpen} drafts={drafts} onUpdateDraft={updateDraftMessage} onSendCurrent={sendCurrent} onSendAll={sendAll} />
            <ConfirmPaymentDialog
                key={confirmPayRow?.visitId || 'no-visit'}
                open={Boolean(confirmPayRow)}
                onOpenChange={(open) => {
                    const hasActiveVisitSettlement = confirmPayRow
                        && Array.from(settlementLocksRef.current)
                            .some((key) => key.startsWith(`${confirmPayRow.visitId}:`));
                    if (!open && (confirmPayLoading || hasActiveVisitSettlement))
                        return;
                    if (!open)
                        setConfirmPayRow(null);
                }}
                loading={confirmPayLoading}
                remainingBalance={confirmPayRow?.defaultAmount || 0}
                allowSupplierBalance={false}
                allowDiscount={false}
                allowMultipleMethods
                onConfirm={async ({ paymentLines, date }) => {
                    if (!confirmPayRow) return;
                    setConfirmPayLoading(true);
                    try {
                        await createSettlementOrder(confirmPayRow, paymentLines, date);
                    }
                    finally {
                        setConfirmPayLoading(false);
                    }
                }}
            />
        </div>
    );
}
